'use client'

import { Tooltip } from '@trustoo/components/Tooltip/v2'
import clsx from 'clsx'
import {
    ChangeEvent,
    ComponentPropsWithoutRef,
    FocusEvent,
    forwardRef,
    ReactNode,
    useCallback,
    useId,
    useRef,
    useState,
} from 'react'

export type InputSize = 'sm' | 'md' | 'lg'
export type InputVariant =
    | 'default'
    | 'leading-dropdown'
    | 'trailing-dropdown'
    | 'leading-text'
    | 'payment-input'
    | 'tags-inner'
    | 'tags-outer'
    | 'tags'
    | 'trailing-button'
    | 'password'
    | 'date-and-time'
    | 'number-counter-horizontal'
    | 'number-counter-vertical'
    | 'file-upload'
export type InputState = 'placeholder' | 'filled' | 'focused' | 'disabled'

export interface InputDropdownOption {
    value: string
    label: ReactNode
    disabled?: boolean
}

export interface InputProps extends Omit<ComponentPropsWithoutRef<'input'>, 'size' | 'type'> {
    size?: InputSize
    variant?: InputVariant
    state?: InputState
    destructive?: boolean
    label?: ReactNode
    hintText?: ReactNode
    hintSuccess?: boolean
    required?: boolean
    helpIcon?: boolean
    helpText?: ReactNode
    iconLeading?: ReactNode
    iconSwap?: ReactNode
    leadingText?: ReactNode
    leadingAddon?: ReactNode
    trailingAddon?: ReactNode
    dropdownOptions?: InputDropdownOption[]
    dropdownValue?: string
    defaultDropdownValue?: string
    onDropdownChange?: (value: string) => void
    dropdownAriaLabel?: string
    trailingButton?: ReactNode
    buttonLabel?: ReactNode
    buttonIcon?: ReactNode
    onButtonClick?: () => void
    tags?: ReactNode
    timeSelector?: boolean
    timeInputProps?: Omit<ComponentPropsWithoutRef<'input'>, 'size'>
    fileName?: ReactNode
    loading?: boolean
    error?: string | boolean
    defaultErrorMsg?: string
    withErrorIcon?: boolean
    className?: string
    inputClassName?: string
    labelClassName?: string
    errorClassName?: string
    type?: string
}

interface SizeClasses {
    field: string
    tagsField: string
    padL: string
    padR: string
    text: string
    icon: string
    counter: string
}

const SIZE_CLASSES: Record<InputSize, SizeClasses> = {
    sm: {
        field: 'h-[34px]',
        tagsField: 'min-h-[34px] py-spacing-xs pl-spacing-sm pr-spacing-lg',
        padL: 'pl-spacing-lg',
        padR: 'pr-spacing-lg',
        text: 'text-sm',
        icon: 'size-4',
        counter: 'w-[34px]',
    },
    md: {
        field: 'h-9',
        tagsField: 'min-h-9 py-spacing-xs pl-spacing-sm pr-spacing-lg',
        padL: 'pl-spacing-lg',
        padR: 'pr-spacing-lg',
        text: 'text-md',
        icon: 'size-5',
        counter: 'w-9',
    },
    lg: {
        field: 'h-10',
        tagsField: 'min-h-10 py-spacing-sm pl-spacing-md pr-3.5',
        padL: 'pl-3.5',
        padR: 'pr-3.5',
        text: 'text-md',
        icon: 'size-5',
        counter: 'w-10',
    },
}

const DEFAULT_BUTTON_LABEL: Partial<Record<InputVariant, string>> = {
    'trailing-button': 'Copy',
    'file-upload': 'Browse',
}

interface IconProps {
    className?: string
}

const StrokeIcon = ({ className, d }: IconProps & { d: string }) => (
    <svg className={clsx('shrink-0', className)} viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path
            d={d}
            stroke="currentColor"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
)

const HelpCircleIcon = ({ className }: IconProps) => (
    <StrokeIcon
        className={className}
        d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3M12 17h.01M22 12c0 5.523-4.477 10-10 10S2 17.523 2 12 6.477 2 12 2s10 4.477 10 10Z"
    />
)

const AlertCircleIcon = ({ className }: IconProps) => (
    <StrokeIcon
        className={className}
        d="M12 8v4M12 16h.01M22 12c0 5.523-4.477 10-10 10S2 17.523 2 12 6.477 2 12 2s10 4.477 10 10Z"
    />
)

const CheckCircleIcon = ({ className }: IconProps) => (
    <StrokeIcon
        className={className}
        d="m7.5 12 3 3 6-6m5.5 3c0 5.523-4.477 10-10 10S2 17.523 2 12 6.477 2 12 2s10 4.477 10 10Z"
    />
)

const ChevronDownIcon = ({ className }: IconProps) => (
    <StrokeIcon className={className} d="m6 9 6 6 6-6" />
)

const ChevronUpIcon = ({ className }: IconProps) => (
    <StrokeIcon className={className} d="m18 15-6-6-6 6" />
)

const MinusIcon = ({ className }: IconProps) => <StrokeIcon className={className} d="M5 12h14" />

const PlusIcon = ({ className }: IconProps) => (
    <StrokeIcon className={className} d="M12 5v14m-7-7h14" />
)

const LockIcon = ({ className }: IconProps) => (
    <StrokeIcon
        className={className}
        d="M7 10V8a5 5 0 0 1 10 0v2m-5 4.5v2M8.8 21h6.4c1.68 0 2.52 0 3.162-.327a3 3 0 0 0 1.311-1.311C20 18.72 20 17.88 20 16.2v-1.4c0-1.68 0-2.52-.327-3.162a3 3 0 0 0-1.311-1.311C17.72 10 16.88 10 15.2 10H8.8c-1.68 0-2.52 0-3.162.327a3 3 0 0 0-1.311 1.311C4 12.28 4 13.12 4 14.8v1.4c0 1.68 0 2.52.327 3.162a3 3 0 0 0 1.311 1.311C6.28 21 7.12 21 8.8 21Z"
    />
)

const EyeIcon = ({ className }: IconProps) => (
    <StrokeIcon
        className={className}
        d="M2.42 12.713c-.136-.215-.204-.323-.242-.49a1.173 1.173 0 0 1 0-.446c.038-.167.106-.274.242-.49C3.546 9.505 6.895 5 12 5s8.455 4.505 9.58 6.287c.137.215.205.323.243.49.029.125.029.322 0 .446-.038.167-.106.274-.242.49C20.455 14.495 17.105 19 12 19c-5.106 0-8.455-4.505-9.58-6.287ZM12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z"
    />
)

const EyeOffIcon = ({ className }: IconProps) => (
    <StrokeIcon
        className={className}
        d="M10.743 5.092A10.142 10.142 0 0 1 12 5c5.105 0 8.455 4.505 9.58 6.287.137.215.205.323.243.49.029.125.029.322 0 .447-.038.166-.107.274-.244.492-.3.474-.757 1.141-1.363 1.865M6.724 6.715c-2.162 1.467-3.63 3.504-4.303 4.57-.137.217-.205.325-.243.492a1.173 1.173 0 0 0 0 .446c.038.167.106.274.242.49C3.546 14.495 6.895 19 12 19c2.059 0 3.832-.732 5.289-1.723M3 3l18 18M9.88 9.879a3 3 0 0 0 4.243 4.243"
    />
)

const CalendarIcon = ({ className }: IconProps) => (
    <StrokeIcon
        className={className}
        d="M21 10H3m13-8v4M8 2v4m-.2 16h8.4c1.68 0 2.52 0 3.162-.327a3 3 0 0 0 1.311-1.311C21 19.72 21 18.88 21 17.2V8.8c0-1.68 0-2.52-.327-3.162a3 3 0 0 0-1.311-1.311C18.72 4 17.88 4 16.2 4H7.8c-1.68 0-2.52 0-3.162.327a3 3 0 0 0-1.311 1.311C3 5.28 3 6.12 3 7.8v9.4c0 1.68 0 2.52.327 3.162a3 3 0 0 0 1.311 1.311C5.28 22 6.12 22 7.8 22Z"
    />
)

const CopyIcon = ({ className }: IconProps) => (
    <StrokeIcon
        className={className}
        d="M5 15c-.932 0-1.398 0-1.765-.152a2 2 0 0 1-1.083-1.083C2 13.398 2 12.932 2 12V5.2c0-1.12 0-1.68.218-2.108a2 2 0 0 1 .874-.874C3.52 2 4.08 2 5.2 2H12c.932 0 1.398 0 1.765.152a2 2 0 0 1 1.083 1.083C15 3.602 15 4.068 15 5m-2.8 17h6.6c1.12 0 1.68 0 2.108-.218a2 2 0 0 0 .874-.874C22 20.48 22 19.92 22 18.8v-6.6c0-1.12 0-1.68-.218-2.108a2 2 0 0 0-.874-.874C20.48 9 19.92 9 18.8 9h-6.6c-1.12 0-1.68 0-2.108.218a2 2 0 0 0-.874.874C9 10.52 9 11.08 9 12.2v6.6c0 1.12 0 1.68.218 2.108a2 2 0 0 0 .874.874C10.52 22 11.08 22 12.2 22Z"
    />
)

const SpinnerIcon = ({ className }: IconProps) => (
    <svg
        className={clsx('shrink-0 animate-spin', className)}
        viewBox="0 0 24 24"
        fill="none"
        aria-hidden="true">
        <circle cx="12" cy="12" r="9" stroke="currentColor" strokeOpacity={0.25} strokeWidth={3} />
        <path
            d="M21 12a9 9 0 0 0-9-9"
            stroke="currentColor"
            strokeWidth={3}
            strokeLinecap="round"
        />
    </svg>
)

const iconButtonClasses = clsx(
    'flex shrink-0 items-center justify-center border-0 bg-base-transparent p-spacing-none',
    'text-foreground-fg-quaternary transition-colors duration-150 ease-in-out',
    'hover:text-foreground-fg-quaternary_hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-focus-ring',
    'disabled:cursor-not-allowed disabled:hover:text-foreground-fg-quaternary',
)

export const Input = forwardRef<HTMLInputElement, InputProps>(
    (
        {
            size = 'sm',
            variant = 'default',
            state = 'placeholder',
            destructive = false,
            label,
            hintText,
            hintSuccess = false,
            required = false,
            helpIcon = false,
            helpText,
            iconLeading,
            iconSwap,
            leadingText,
            leadingAddon,
            trailingAddon,
            dropdownOptions,
            dropdownValue,
            defaultDropdownValue,
            onDropdownChange,
            dropdownAriaLabel,
            trailingButton,
            buttonLabel,
            buttonIcon,
            onButtonClick,
            tags,
            timeSelector = true,
            timeInputProps,
            fileName,
            loading = false,
            error,
            defaultErrorMsg,
            withErrorIcon = false,
            className,
            inputClassName,
            labelClassName,
            errorClassName,
            type = 'text',
            id: providedId,
            value,
            disabled,
            readOnly,
            placeholder,
            onFocus: onFocusProp,
            onBlur: onBlurProp,
            onChange: onChangeProp,
            ...restInputProps
        },
        ref,
    ) => {
        const generatedId = useId()
        const id = providedId || generatedId
        const hintId = `${id}-hint`
        const sizeClasses = SIZE_CLASSES[size]

        const innerRef = useRef<HTMLInputElement | null>(null)
        const [passwordVisible, setPasswordVisible] = useState(false)
        const [selectedFileName, setSelectedFileName] = useState<string | undefined>(undefined)

        const setRefs = useCallback(
            (node: HTMLInputElement | null) => {
                innerRef.current = node
                if (typeof ref === 'function') {
                    ref(node)
                } else if (ref) {
                    ref.current = node
                }
            },
            [ref],
        )

        const isDisabled = Boolean(disabled) || state === 'disabled'
        const isForcedFocus = !isDisabled && state === 'focused'
        const hasError = Boolean(error) || destructive
        const isTagsInner = variant === 'tags-inner' || variant === 'tags'
        const isTagsOuter = variant === 'tags-outer'
        const isPassword = variant === 'password'
        const isFileUpload = variant === 'file-upload'
        const isCounterHorizontal = variant === 'number-counter-horizontal'
        const isCounterVertical = variant === 'number-counter-vertical'
        const isCounter = isCounterHorizontal || isCounterVertical

        const getInputType = () => {
            if (isPassword) {
                return passwordVisible ? 'text' : 'password'
            }
            if (isFileUpload) {
                return 'file'
            }
            if (isCounter) {
                return 'number'
            }
            return type
        }

        const onFocus = (e: FocusEvent<HTMLInputElement>) => {
            if (readOnly) {
                e.currentTarget.blur()
            }
            onFocusProp?.(e)
        }

        const onBlur = (e: FocusEvent<HTMLInputElement>) => {
            onBlurProp?.(e)
        }

        const onChange = (e: ChangeEvent<HTMLInputElement>) => {
            if (isFileUpload) {
                const files = e.target.files
                setSelectedFileName(files && files.length > 0 ? files[0].name : undefined)
            }
            onChangeProp?.(e)
        }

        const step = (direction: 'up' | 'down') => {
            const node = innerRef.current
            if (!node || isDisabled || readOnly) {
                return
            }
            if (direction === 'up') {
                node.stepUp()
            } else {
                node.stepDown()
            }
            node.dispatchEvent(new Event('input', { bubbles: true }))
        }

        const getErrorMessage = (): string | undefined => {
            if (typeof error === 'string') {
                return error
            }
            if (error) {
                return defaultErrorMsg || 'This field has an error'
            }
            return undefined
        }
        const errorMessage = getErrorMessage()

        const getBorderClasses = () => {
            if (hasError && isForcedFocus) {
                return 'border-border-error ring-1 ring-inset ring-focus-ring-error'
            }
            if (hasError) {
                return 'border-border-error-subtle focus-within:border-border-error focus-within:ring-1 focus-within:ring-inset focus-within:ring-focus-ring-error'
            }
            if (isForcedFocus) {
                return 'border-border-brand ring-1 ring-inset ring-focus-ring'
            }
            return 'border-border-primary focus-within:border-border-brand focus-within:ring-1 focus-within:ring-inset focus-within:ring-focus-ring'
        }

        const fieldClasses = clsx(
            'relative flex w-full overflow-hidden rounded-radius-md border border-solid bg-background-bg-primary shadow-xs',
            'transition-colors duration-150 ease-in-out',
            getBorderClasses(),
            isTagsInner
                ? clsx('flex-wrap items-center gap-spacing-sm', sizeClasses.tagsField)
                : clsx('items-stretch', sizeClasses.field),
            isDisabled && 'cursor-not-allowed opacity-50',
        )

        const inputBaseClasses = clsx(
            'min-w-0 flex-1 border-0 bg-base-transparent p-spacing-none font-body font-regular outline-none',
            'text-text-primary placeholder:text-text-placeholder',
            'overflow-hidden text-ellipsis disabled:cursor-not-allowed',
            sizeClasses.text,
        )

        const describedBy = errorMessage || hintText ? hintId : undefined
        const inputValueProps = isFileUpload ? {} : { value }

        const renderInput = (extraClasses?: string) => (
            <input
                {...restInputProps}
                {...inputValueProps}
                ref={setRefs}
                id={id}
                type={getInputType()}
                disabled={isDisabled}
                readOnly={readOnly}
                required={required}
                placeholder={placeholder}
                aria-invalid={hasError || undefined}
                aria-describedby={describedBy}
                onFocus={onFocus}
                onBlur={onBlur}
                onChange={onChange}
                className={clsx(
                    inputBaseClasses,
                    isCounter &&
                        '[appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none',
                    isFileUpload && 'sr-only',
                    extraClasses,
                    inputClassName,
                )}
            />
        )

        const renderLeadingIcon = () => {
            const icon = iconLeading ?? iconSwap
            if (!icon) {
                return null
            }
            return (
                <span
                    className={clsx(
                        'flex shrink-0 items-center justify-center text-foreground-fg-quaternary',
                        sizeClasses.icon,
                    )}>
                    {icon}
                </span>
            )
        }

        const renderIndicator = () => {
            if (hasError) {
                return <AlertCircleIcon className="size-4 text-foreground-fg-error-secondary" />
            }
            if (!helpIcon) {
                return null
            }
            if (helpText) {
                return (
                    <Tooltip title={helpText}>
                        <button
                            type="button"
                            aria-label="Help"
                            className={clsx(iconButtonClasses, 'cursor-help rounded-radius-full')}>
                            <HelpCircleIcon className="size-4" />
                        </button>
                    </Tooltip>
                )
            }
            return (
                <span className="inline-flex shrink-0 cursor-help text-foreground-fg-quaternary">
                    <HelpCircleIcon className="size-4" />
                </span>
            )
        }

        const renderDropdown = () => {
            if (!dropdownOptions || dropdownOptions.length === 0) {
                return null
            }
            const selectValueProps =
                dropdownValue === undefined
                    ? { defaultValue: defaultDropdownValue }
                    : { value: dropdownValue }
            return (
                <span className="relative flex items-center">
                    <select
                        {...selectValueProps}
                        aria-label={dropdownAriaLabel}
                        disabled={isDisabled}
                        onChange={(e) => onDropdownChange?.(e.target.value)}
                        className={clsx(
                            'cursor-pointer appearance-none border-0 bg-base-transparent py-spacing-none pl-spacing-none pr-spacing-2xl font-body font-regular outline-none',
                            'text-text-primary disabled:cursor-not-allowed',
                            sizeClasses.text,
                        )}>
                        {dropdownOptions.map((option) => (
                            <option
                                key={option.value}
                                value={option.value}
                                disabled={option.disabled}>
                                {option.label}
                            </option>
                        ))}
                    </select>
                    <ChevronDownIcon className="pointer-events-none absolute right-0 size-4 text-foreground-fg-quaternary" />
                </span>
            )
        }

        const renderSegmentButton = () => {
            if (trailingButton) {
                return (
                    <div className="flex shrink-0 items-center pr-spacing-xs">{trailingButton}</div>
                )
            }
            const defaultIcon =
                variant === 'trailing-button' ? <CopyIcon className="size-4" /> : null
            return (
                <button
                    type="button"
                    disabled={isDisabled}
                    onClick={() => {
                        if (isFileUpload) {
                            innerRef.current?.click()
                        }
                        onButtonClick?.()
                    }}
                    className={clsx(
                        'flex shrink-0 items-center gap-spacing-xs border-0 border-l border-solid border-border-primary bg-background-bg-primary',
                        'font-body font-semibold text-text-secondary transition-colors duration-150 ease-in-out',
                        'hover:bg-background-bg-primary_hover hover:text-text-secondary_hover',
                        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-focus-ring',
                        'disabled:cursor-not-allowed disabled:hover:bg-background-bg-primary',
                        sizeClasses.padL,
                        sizeClasses.padR,
                        sizeClasses.text,
                    )}>
                    {buttonIcon ?? defaultIcon}
                    {buttonLabel ?? DEFAULT_BUTTON_LABEL[variant]}
                </button>
            )
        }

        const contentClasses = clsx('flex min-w-0 flex-1 items-center gap-spacing-md')

        const renderFieldContent = () => {
            switch (variant) {
                case 'leading-dropdown':
                    return (
                        <>
                            <div className={clsx('flex shrink-0 items-center', sizeClasses.padL)}>
                                {leadingAddon ?? renderDropdown()}
                            </div>
                            <div
                                className={clsx(contentClasses, 'pl-spacing-md', sizeClasses.padR)}>
                                {renderInput()}
                                {renderIndicator()}
                            </div>
                        </>
                    )
                case 'trailing-dropdown':
                    return (
                        <>
                            <div
                                className={clsx(contentClasses, sizeClasses.padL, 'pr-spacing-md')}>
                                {renderLeadingIcon()}
                                {renderInput()}
                                {renderIndicator()}
                            </div>
                            <div className={clsx('flex shrink-0 items-center', sizeClasses.padR)}>
                                {trailingAddon ?? renderDropdown()}
                            </div>
                        </>
                    )
                case 'leading-text':
                    return (
                        <>
                            <div
                                className={clsx(
                                    'flex shrink-0 items-center pr-spacing-lg font-body font-regular text-text-tertiary',
                                    sizeClasses.padL,
                                    sizeClasses.text,
                                )}>
                                {leadingText ?? 'https://'}
                            </div>
                            <div
                                className={clsx(
                                    contentClasses,
                                    'border-0 border-l border-solid border-border-primary',
                                    sizeClasses.padL,
                                    sizeClasses.padR,
                                )}>
                                {renderInput()}
                                {renderIndicator()}
                            </div>
                        </>
                    )
                case 'payment-input':
                    return (
                        <div className={clsx(contentClasses, sizeClasses.padL, sizeClasses.padR)}>
                            <span className="flex h-6 w-[34px] shrink-0 items-center justify-center overflow-hidden rounded-radius-xs border border-solid border-border-secondary bg-background-bg-primary text-foreground-fg-quaternary">
                                {iconLeading ?? iconSwap}
                            </span>
                            {renderInput()}
                            {renderIndicator()}
                        </div>
                    )
                case 'tags-inner':
                case 'tags':
                    return (
                        <>
                            {tags}
                            {renderInput('min-w-20')}
                            {renderIndicator()}
                        </>
                    )
                case 'trailing-button':
                    return (
                        <>
                            <div
                                className={clsx(contentClasses, sizeClasses.padL, 'pr-spacing-md')}>
                                {renderLeadingIcon()}
                                {renderInput()}
                                {renderIndicator()}
                            </div>
                            {renderSegmentButton()}
                        </>
                    )
                case 'file-upload':
                    return (
                        <>
                            <div
                                className={clsx(contentClasses, sizeClasses.padL, 'pr-spacing-md')}>
                                {renderInput()}
                                <span
                                    className={clsx(
                                        'min-w-0 flex-1 truncate font-body font-regular',
                                        sizeClasses.text,
                                        fileName || selectedFileName
                                            ? 'text-text-primary'
                                            : 'text-text-placeholder',
                                    )}>
                                    {fileName ??
                                        selectedFileName ??
                                        placeholder ??
                                        'No file chosen'}
                                </span>
                                {loading && (
                                    <SpinnerIcon className="size-4 text-foreground-fg-brand-tertiary" />
                                )}
                                {renderIndicator()}
                            </div>
                            {renderSegmentButton()}
                        </>
                    )
                case 'password':
                    return (
                        <div className={clsx(contentClasses, sizeClasses.padL, sizeClasses.padR)}>
                            <LockIcon
                                className={clsx(sizeClasses.icon, 'text-foreground-fg-quaternary')}
                            />
                            {renderInput()}
                            <button
                                type="button"
                                disabled={isDisabled}
                                aria-label={passwordVisible ? 'Hide password' : 'Show password'}
                                aria-pressed={passwordVisible}
                                onClick={() => setPasswordVisible((visible) => !visible)}
                                className={clsx(iconButtonClasses, 'rounded-radius-xs')}>
                                {passwordVisible ? (
                                    <EyeOffIcon className="size-4" />
                                ) : (
                                    <EyeIcon className="size-4" />
                                )}
                            </button>
                        </div>
                    )
                case 'date-and-time':
                    return (
                        <div className={clsx(contentClasses, sizeClasses.padL, sizeClasses.padR)}>
                            <CalendarIcon
                                className={clsx(sizeClasses.icon, 'text-foreground-fg-quaternary')}
                            />
                            {renderInput(timeSelector ? 'grow-0 basis-28' : undefined)}
                            {timeSelector && (
                                <>
                                    <span
                                        className={clsx(
                                            'shrink-0 font-body text-text-tertiary',
                                            sizeClasses.text,
                                        )}
                                        aria-hidden="true">
                                        –
                                    </span>
                                    <input
                                        placeholder="00:00"
                                        aria-label="Time"
                                        {...timeInputProps}
                                        type={timeInputProps?.type ?? 'text'}
                                        disabled={isDisabled}
                                        readOnly={readOnly}
                                        className={clsx(
                                            inputBaseClasses,
                                            timeInputProps?.className,
                                        )}
                                    />
                                </>
                            )}
                            {renderIndicator()}
                        </div>
                    )
                case 'number-counter-horizontal':
                    return (
                        <>
                            <button
                                type="button"
                                disabled={isDisabled}
                                aria-label="Decrease"
                                onClick={() => step('down')}
                                className={clsx(
                                    iconButtonClasses,
                                    'hover:bg-background-bg-primary_hover',
                                    sizeClasses.counter,
                                )}>
                                <MinusIcon className={sizeClasses.icon} />
                            </button>
                            <div className={clsx(contentClasses, 'justify-center px-spacing-md')}>
                                {renderInput('text-center')}
                            </div>
                            <button
                                type="button"
                                disabled={isDisabled}
                                aria-label="Increase"
                                onClick={() => step('up')}
                                className={clsx(
                                    iconButtonClasses,
                                    'hover:bg-background-bg-primary_hover',
                                    sizeClasses.counter,
                                )}>
                                <PlusIcon className={sizeClasses.icon} />
                            </button>
                        </>
                    )
                case 'number-counter-vertical':
                    return (
                        <>
                            <div
                                className={clsx(contentClasses, sizeClasses.padL, 'pr-spacing-md')}>
                                {renderInput()}
                            </div>
                            <div className="flex w-6 shrink-0 flex-col border-0 border-l border-solid border-border-primary">
                                <button
                                    type="button"
                                    disabled={isDisabled}
                                    aria-label="Increase"
                                    onClick={() => step('up')}
                                    className={clsx(
                                        iconButtonClasses,
                                        'flex-1 hover:bg-background-bg-primary_hover',
                                    )}>
                                    <ChevronUpIcon className="size-3" />
                                </button>
                                <button
                                    type="button"
                                    disabled={isDisabled}
                                    aria-label="Decrease"
                                    onClick={() => step('down')}
                                    className={clsx(
                                        iconButtonClasses,
                                        'flex-1 border-0 border-t border-solid border-border-primary hover:bg-background-bg-primary_hover',
                                    )}>
                                    <ChevronDownIcon className="size-3" />
                                </button>
                            </div>
                        </>
                    )
                case 'tags-outer':
                case 'default':
                default:
                    return (
                        <div className={clsx(contentClasses, sizeClasses.padL, sizeClasses.padR)}>
                            {renderLeadingIcon()}
                            {renderInput()}
                            {renderIndicator()}
                        </div>
                    )
            }
        }

        const getHintIconClasses = () => {
            if (destructive) {
                return 'text-foreground-fg-error-secondary'
            }
            if (hintSuccess) {
                return 'text-foreground-fg-success-primary'
            }
            return 'text-foreground-fg-quaternary'
        }

        const renderMessage = () => {
            if (errorMessage) {
                return (
                    <div
                        id={hintId}
                        className={clsx(
                            'flex items-center gap-spacing-xs font-body text-sm font-regular text-text-error',
                            errorClassName,
                        )}>
                        {withErrorIcon && <AlertCircleIcon className="size-4" />}
                        {errorMessage}
                    </div>
                )
            }
            if (!hintText) {
                return null
            }
            return (
                <div
                    id={hintId}
                    className={clsx(
                        'flex items-center gap-spacing-xs font-body text-sm font-regular',
                        destructive ? 'text-text-error' : 'text-text-tertiary',
                    )}>
                    {isPassword && (
                        <CheckCircleIcon className={clsx('size-4', getHintIconClasses())} />
                    )}
                    {hintText}
                </div>
            )
        }

        const showLabelRow = Boolean(label)

        return (
            <div
                className={clsx('flex w-full flex-col gap-spacing-sm font-body', className)}
                data-state={isDisabled ? 'disabled' : state}
                data-field-has-error={hasError || undefined}>
                {showLabelRow && (
                    <div className={clsx('flex items-center gap-spacing-xxs', labelClassName)}>
                        <label htmlFor={id} className="text-sm font-medium text-text-secondary">
                            {label}
                        </label>
                        {required && (
                            <span
                                className="text-sm font-medium text-text-brand-tertiary"
                                aria-hidden="true">
                                *
                            </span>
                        )}
                    </div>
                )}

                <div className={fieldClasses}>{renderFieldContent()}</div>

                {isTagsOuter && tags && <div className="flex flex-wrap gap-spacing-sm">{tags}</div>}

                {renderMessage()}
            </div>
        )
    },
)

Input.displayName = 'Input'
